import { useState, useContext, useEffect, useCallback } from 'react'

import { doesCompanyHasPinflagPayments } from '../../components/PaymentMethodSelection'
import { DataContext } from '../../context/DataContext'
import { SHOPIFY_PLUS_STORES } from '../../constants/stores'
import { gaCompletedPaymentSelectionPostMessage } from '../../helpers/gaEventsHelper'
import { scrollToBottom } from '../../helpers/utils'
import { useCmsAdapter } from '../useCmsAdapter'
import { useMagento } from '../useMagento'
import { useShopify } from '../useShopify'
import { useSteps } from '../useSteps'
import { useVtex } from '../useVtex'
import { useWoocommerce } from '../useWoocommerce'

import { usePayment } from './usePayment/usePayment'

export const useNativePayment = ({ scrapperClient, effectsActive = true }) => {
  const {
    ecommerceInfo,
    setShowFinalLoader,
    cartData,
    cmsPaymentMethods,
    companyInfo,
    setIsPickup
  } = useContext(DataContext)

  const {
    selectedNativePaymentMethod,
    setSelectedNativePaymentMethod,
    setIsLoadingGatewayForm,
    hideOtherPaymentOptions
  } = usePayment()
  const { companyName, ownPayments } = companyInfo

  const [selectedPaymentMethodData, setSelectedPaymentMethodData] =
    useState('')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingNativePaymentMethods, setIsLoadingNativePaymentMethods] =
    useState(true)
  const [isLoadingScrapper, setIsLoadingScrapper] = useState(true)

  const { markDeliveryStepAsCompleted } = useSteps()
  const { setInformationMagento } = useMagento()
  const { shippingPostMessageWoocommerce, getPaymentMethodsWoocommerce } =
    useWoocommerce()
  const {
    addPaymentDataVtex,
    goToPaymentVtexIO,
    saveShippingInformationVtex,
    getPaymentMethodsVtexIO
  } = useVtex()
  const { removeIframeShopify, getShopifyConfiguration } = useShopify()
  const { trackingThirdStep } = useCmsAdapter()

  const handleMagentoAndWooCommerceShipping = async () => {
    if (
      ecommerceInfo.cms === 'magento' ||
      ecommerceInfo.cms.includes('woocommerce')
    ) {
      setIsSubmitting(true)
      if (ecommerceInfo.cms === 'magento') await setInformationMagento()
      if (ecommerceInfo.cms.includes('woocommerce')) {
        shippingPostMessageWoocommerce()
      }
    }
  }

  const trackPaymentSelection = useCallback(() => {
    trackingThirdStep({
      paymentMethod: selectedNativePaymentMethod,
      timeEndThirdStep: new Date()
    })
  }, [selectedNativePaymentMethod])

  const determineGroupName = () => {
    if (cartData.total === 0 && cartData.giftCard.applied > 0) {
      return 'gift-card'
    }
    return `#payment-group-${selectedPaymentMethodData.groupName}`
  }

  const payWithNativeMethod = async () => {
    gaCompletedPaymentSelectionPostMessage()
    await handleMagentoAndWooCommerceShipping()

    if (
      ecommerceInfo.cms !== 'magento' &&
      !ecommerceInfo.cms.includes('woocommerce')
    ) {
      setShowFinalLoader(true)
    }

    trackPaymentSelection()

    const groupName = determineGroupName()
    await goToPaymentAndPostMessage(groupName)
  }

  const goToPaymentAndPostMessage = async (groupName) => {
    await addPaymentDataVtex(selectedPaymentMethodData, cartData.total)
    await goToPaymentVtexIO(groupName)
    window.parent.postMessage(
      {
        type: 'go-to-payment',
        origin: 'pinflag-checkout-iframe',
        message: { groupName }
      },
      '*'
    )
  }

  const handlePaymentMethod = (paymentMethod) => {
    setSelectedPaymentMethodData(paymentMethod)
    setSelectedNativePaymentMethod(paymentMethod.code)
    if (
      ['Depósito Bancario', 'Transferencia Bancaria', 'Transferencia'].includes(
        paymentMethod.code
      )
    ) {
      setTimeout(() => scrollToBottom('div-payment-methods'), 300)
    }
  }

  const handlePaymentMethodSelectionForOtherPayments = () => {
    setIsLoadingGatewayForm(false)
  }

  useEffect(() => {
    if (!effectsActive) return
    if (cmsPaymentMethods.length) {
      handlePaymentMethod(cmsPaymentMethods[0])
    } else {
      getPaymentMethodsWoocommerce()
    }
  }, [cmsPaymentMethods, effectsActive])

  useEffect(() => {
    if (!effectsActive) return
    if (!cmsPaymentMethods.length) return
    if (
      SHOPIFY_PLUS_STORES.includes(companyName) ||
      (ecommerceInfo.cms !== 'shopify' && !ecommerceInfo.cms.includes('vtex'))
    ) {
      setIsLoadingNativePaymentMethods(false)
    }
    if (
      scrapperClient.includes('information') ||
      scrapperClient.includes('discountCodes')
    ) {
      setTimeout(() => setIsLoadingNativePaymentMethods(false), 2000)
    }
    if (scrapperClient.includes('payment')) {
      setIsLoadingNativePaymentMethods(false)
    }
  }, [scrapperClient, effectsActive])

  useEffect(() => {
    if (!effectsActive) return
    const limitScrapper = 12000
    const timer = setTimeout(() => {
      if (isLoadingNativePaymentMethods) {
        removeIframeShopify(
          {},
          {},
          'redirect to checkout - scraper disconnected'
        )
      }
    }, limitScrapper)

    return () => clearTimeout(timer)
  }, [isLoadingNativePaymentMethods])

  useEffect(() => {
    if (!effectsActive) return
    setIsPickup(false)
    if (ecommerceInfo.cms === 'shopify' && !cmsPaymentMethods.length) {
      getShopifyConfiguration().then((res) => {
        if (!res) {
          if (doesCompanyHasPinflagPayments(ownPayments)) hideOtherPaymentOptions()
          removeIframeShopify({}, {}, 'redirect to checkout')
        } else {
          setIsLoadingScrapper(false)
        }
      })
    }
  }, [effectsActive])

  // Vtex useEffect
  useEffect(() => {
    if (!effectsActive) return
    saveShippingInformationVtex().then(() => {
      markDeliveryStepAsCompleted()
      getPaymentMethodsVtexIO(ecommerceInfo.vtexOrderFormId)
    })
  }, [effectsActive])

  useEffect(() => {
    if (!effectsActive) return
    getPaymentMethodsWoocommerce()
  }, [effectsActive])

  return {
    isSubmitting,
    isLoadingScrapper,
    payWithNativeMethod,
    setSelectedPaymentMethodData,
    handlePaymentMethod,
    handlePaymentMethodSelectionForOtherPayments,
    selectedNativePaymentMethod,
    setSelectedNativePaymentMethod
  }
}
